/**
 *
 * Account we can probably forget about, since that's not relevant for the first one
 *
 * Get a list of the existing bank accounts
 */

import BankFetcher from '../DataFetchers/bank-fetcher';
import { useContext } from 'react';
import { useEffect, useState } from 'react';
import { getClient } from '../../../api-utils/general-utils';
import {
  BankAccount,
  CreateBankAccountDto,
  UpdateAccountDto,
  UpdateBankAccountDto,
} from '../../../api-utils/generated-client';
import { MessageContext } from '../../../context';
import FooterButtons from '../../../Footer/footer-buttons';
import TitleSection from '../../../Header/title-section';
import PayroInput from '../../../widgets/PayroInput';
import './index.scss';
import { v4 as uuidv4 } from 'uuid';
import Loader from '../../../widgets/Loader';
import PayroRadioButtonGroup from '../../../widgets/PayroRadioButtonGroup';
import bankInfo from '../../../common-components/bank-account/bank-info';
import addimg from '../../../common-icons/add.svg';
import repayment from '../../Ledger/LedgerHome/Repayments/repayment';
import RequestFundingTitle from '../request-funding-title';
import { isFeatureOn } from '../../../utils';
import {
  useRecoilValue,
  useSetRecoilState,
  useRecoilState,
  useResetRecoilState,
} from 'recoil';
import {
  FundingStepStateType,
  depositBankRoutingNumberConfirmState,
  depositBankAccountNumberConfirmState,
  withdrawalBankRoutingNumberConfirmState,
  withdrawalBankAccountNumberConfirmState,
  fundingStepState,
  bankAccountsState,
  dealDraftState,
  isManualBankState,
  isAllBankInfoValidState,
  needsManualFormState,
  bankAccountToUpdateState,
  bankAccountToCreateState,
  chaseBankAccountsArrayState,
  chaseBankAccountTempState,
  chaseBankAccountToUpdateState,
  banksToVerifyState,
} from '../../../recoil-state/request-funding-states';
import PlaidConnector from '../../../widgets/PlaidConnector';
import CAPlaidConnector from '../../../widgets/PlaidConnector/ca_index';
import { GetAccountDto } from '../../../api-utils/generated-client';
import {
  isPlaidConnectedState,
  accountRecordState,
} from '../../../recoil-state/general-states';
import ManualBankForm from '../../../widgets/PlaidConnector/ManualBankForm';
import ManualCABankForm from '../../../widgets/PlaidConnector/ManualCABankForm';
import { isNextButtonDisabledState } from '../../../recoil-state/application-stage-states';

/*
This component updates it's state on every change,
 but the actual bank info is not saved to the server until the user navigates
 either next or back.

*/

type SameOrDifferentAccount =
  | 'same-account'
  | 'different-account'
  | '';

export default (props: any) => {
  const banksToVerifyArray = useRecoilValue(banksToVerifyState);
  const setisValid = useSetRecoilState(isAllBankInfoValidState);
  const [needsManualForm, setNeedsManualForm] = useRecoilState(
    needsManualFormState,
  );

  const [isPlaidConnected, setIsPlaidConnected] = useRecoilState(
    isPlaidConnectedState,
  );
  const [isNextDisabled, setIsNextDisabled] = useRecoilState(
    isNextButtonDisabledState,
  );
  const [showbanks, setShowBanks] = useState<boolean>(false);
  const [isManual, setIsManual] = useRecoilState(isManualBankState);

  const [bankAccounts, setBankAccounts] =
    useRecoilState(bankAccountsState);
  const [
    depositAccountNumberConfirm,
    setDepositAccountNumberConfirm,
  ] = useRecoilState(depositBankAccountNumberConfirmState);
  const [
    depositRoutingNumberConfirm,
    setDepositRoutingNumberConfirm,
  ] = useRecoilState(depositBankRoutingNumberConfirmState);
  const [
    withdrawalAccountNumberConfirm,
    setWithdrawalAccountNumberConfirm,
  ] = useRecoilState(withdrawalBankAccountNumberConfirmState);
  const [
    withdrawalRoutingNumberConfirm,
    setWithdrawalRoutingNumberConfirm,
  ] = useRecoilState(withdrawalBankRoutingNumberConfirmState);
  const [dealRecord, setDealRecord] = useRecoilState(dealDraftState);
  const [chaseBankAccountsArray, setChaseBankAccountsArray] =
    useRecoilState(chaseBankAccountsArrayState);

  const messageContext = useContext(MessageContext);

  const [AccountDetail, setAccountDetail] =
    useRecoilState<GetAccountDto>(accountRecordState);

  const [
    useSameAccountForWithdrawal,
    setUseSameAccountForWithdrawal,
  ] = useState<SameOrDifferentAccount>('');

  const [chaseBankAccountToUpdate, setChaseBankAccountToUpdate] =
    useRecoilState<UpdateBankAccountDto>(
      chaseBankAccountToUpdateState,
    );

  useEffect(() => {
    const initializeBankAccounts = async () => {
      if (bankAccounts && bankAccounts.length > 0) {
        return;
      }
      const client = await getClient();
      if (client) {
        const bankAccountsRes =
          await client.bankAccountsControllerFindAll();
        if (bankAccountsRes.data.length < 1) {
          const newUuid = uuidv4();
          setBankAccounts([{ uuid: newUuid }]);
          setDealRecord({
            ...dealRecord,
            deposit_bank_account: newUuid,
          });
        } else {
          setBankAccounts(bankAccountsRes.data as BankAccount[]);
          if (dealRecord.deposit_bank_account) {
            const depositAccount = bankAccountsRes.data.find(
              (ba) => ba.uuid === dealRecord.deposit_bank_account,
            );
            setDepositRoutingNumberConfirm(
              depositAccount?.bank_routing_number ?? '',
            );
            setDepositAccountNumberConfirm(
              depositAccount?.bank_account_number ?? '',
            );
          }
          if (
            dealRecord.withdrawal_bank_account &&
            dealRecord.withdrawal_bank_account !==
              dealRecord.deposit_bank_account
          ) {
            const withdrawalAccount = bankAccountsRes.data.find(
              (ba) => ba.uuid === dealRecord.withdrawal_bank_account,
            );
            setWithdrawalRoutingNumberConfirm(
              withdrawalAccount?.bank_routing_number ?? '',
            );
            setWithdrawalAccountNumberConfirm(
              withdrawalAccount?.bank_account_number ?? '',
            );
          }
        }
      }
    };
    const checkForSameOrDifferentWithdrawal = () => {
      if (
        dealRecord.deposit_bank_account &&
        dealRecord.withdrawal_bank_account
      ) {
        if (
          dealRecord.deposit_bank_account !==
          dealRecord.withdrawal_bank_account
        ) {
          setUseSameAccountForWithdrawal('different-account');
        } else {
          setUseSameAccountForWithdrawal('same-account');
        }
      } else {
        setUseSameAccountForWithdrawal('');
      }
    };

    initializeBankAccounts().then(() => {
      checkForSameOrDifferentWithdrawal();
    });
  }, []);

  useEffect(() => {
    if (bankAccounts && bankAccounts?.length > 0) {
      if (
        !bankAccounts[0].bank_name ||
        bankAccounts[0].bank_name == ''
      ) {
        setShowBanks(false);
      } else {
        setShowBanks(true);
        setIsPlaidConnected('yes');
      }
    }
  }, [bankAccounts]);
  useEffect(() => {
    if (isPlaidConnected === 'unknown') {
      setIsPlaidConnected('no');
    }
  }, []);

  {
    needsManualForm && (
      <ManualBankForm

      //key={bankAccountToUpdate.uuid}
      ></ManualBankForm>
    );
  }

  if (!bankAccounts || bankAccounts.length == 0) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  const plaidAccounts = bankAccounts.filter(
    (account) => account.plaid_id,
  );

  return (
    <div>
      <>
        <RequestFundingTitle
          section={'onboarding'}
          title="Connect Bank Account"
          subtitle="Connect bank account for payroll funding"
          sectionNumber={3}
        />
        {/* } */}
        {process.env.REACT_APP_SHOW_PLAID == 'yes' &&
          isPlaidConnected == 'no' && (
            <>
              <div className="connect-plaid-sec">
                {AccountDetail && AccountDetail.mca_account ? (
                  <CAPlaidConnector />
                ) : (
                  <PlaidConnector />
                )}
              </div>
            </>
          )}
        {showbanks ? (
          <>
            <p id="connectacc">Select bank:</p>

            {bankAccounts &&
              bankAccounts?.length > 0 &&
              bankAccounts.map((bank) => (
                <div
                  className={`section-bb ${
                    dealRecord &&
                    dealRecord.deposit_bank_account == bank.uuid
                      ? 'selected'
                      : ''
                  }`}
                  onClick={async () => {
                    {
                      bank.bank_name &&
                      banksToVerifyArray.includes(
                        bank.bank_name?.toLocaleLowerCase(),
                      ) &&
                      !bank.is_real_bank_numbers
                        ? (setChaseBankAccountToUpdate({
                            ...chaseBankAccountToUpdate,
                            uuid: bank.uuid,
                            bank_name: bank.bank_name,
                            account_last_four: bank.account_last_four,
                            bank_account_number: '',
                            bank_routing_number: '',
                          }),
                          setNeedsManualForm(true))
                        : // console.log(
                          //   'chase',
                          //   chaseBankAccountToUpdate.bank_routing_number,
                          //   chaseBankAccountToUpdate.bank_account_number,
                          // ))
                          setNeedsManualForm(false);
                      // console.log('nonchase');
                    }

                    setDealRecord({
                      ...dealRecord,
                      deposit_bank_account: bank.uuid,
                      withdrawal_bank_account: bank.uuid,
                    });

                    setIsNextDisabled(false);
                    setisValid(true);
                  }}
                >
                  <div className="connectaccouterbox">
                    <div className="testtext">
                      <div className="imgcircle"></div>
                      <div className="connectaccbox">
                        <p id="chasee">
                          {bank.bank_name} ••••{' '}
                          {bank.account_last_four}
                        </p>
                        <p id="checkk">{bank.bank_account_type}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </>
        ) : (
          ''
        )}
        {(isPlaidConnected === 'no' ||
          isPlaidConnected === 'unknown') &&
        !isManual ? (
          <div>
            <p className="manual-sec">
              Having trouble connecting your account?{' '}
              <span
                className="a-manual"
                onClick={() => setIsManual(!isManual)}
              >
                Connect Manually
              </span>
            </p>
          </div>
        ) : !isManual ? (
          <div className="another-add">
            <button
              className="another manual-sec"
              onClick={() => setIsManual(!isManual)}
            >
              <img className="add-img" src={addimg} />
              Add another bank account
            </button>
          </div>
        ) : (
          ''
        )}

        {isManual && (
          <div className="connect-manual">
            <div className="head-sec">
              <h1 className="c-title"> Connect Bank Manually </h1>
              <span
                className="cnct-cancel"
                onClick={() => setIsManual(!isManual)}
              >
                Cancel
              </span>
            </div>
            <ManualCABankForm />
          </div>
        )}
      </>

      <FooterButtons
        isManualBank={isManual}
        mca_account={props.mca_account}
        //userAddressState={props.userAddressState}
        nextDisabled={false}
      />
    </div>
  );
};
